/**
 * Image Optimization Utilities
 * 
 * Provides web-optimized image URLs with resolution reduction
 * and compression for improved performance.
 * 
 * Target specifications:
 * - Max width: 1920px (full), 800px (thumbnail), 400px (card)
 * - Quality: 80% (WebP preferred)
 * - Target file size: 200-400KB max
 */

/**
 * Generates an optimized image URL with resizing and compression parameters
 * @param {string} url - Original image URL
 * @param {object} options - Optimization options
 * @param {number} options.width - Target width in pixels (default: 1920)
 * @param {number} options.quality - Compression quality 1-100 (default: 80)
 * @param {string} options.format - Output format: 'webp', 'jpeg', 'auto' (default: 'webp')
 * @returns {string} Optimized image URL
 */
export const getOptimizedImageUrl = (url, options = {}) => {
  if (!url) return '';
  
  const {
    width = 1920,
    quality = 80,
    format = 'webp'
  } = options;
  
  try {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}w=${width}&q=${quality}&fm=${format}&fit=max`;
  } catch (e) {
    return url;
  }
};

/**
 * Preset configurations for different use cases
 */
export const ImagePresets = {
  // Full resolution for featured display and modals
  full: { width: 2400, quality: 90, format: 'webp' },
  
  // Gallery cards in grid view
  card: { width: 600, quality: 80, format: 'webp' },
  
  // List view thumbnails
  thumbnail: { width: 400, quality: 75, format: 'webp' },
  
  // Featured maps scroll section
  featured: { width: 1600, quality: 85, format: 'webp' },
  
  // Modal/detail view - HIGH RESOLUTION pentru zoom
  detail: { width: 2400, quality: 92, format: 'webp' },
  
  // Mobile detail view - pentru zoom pe mobil
  detailMobile: { width: 2000, quality: 90, format: 'webp' }
};

/**
 * Get optimized URL using preset
 * @param {string} url - Original image URL
 * @param {string} preset - Preset name: 'full', 'card', 'thumbnail', 'featured', 'detail'
 * @returns {string} Optimized image URL
 */
export const getImageWithPreset = (url, preset = 'full') => {
  const config = ImagePresets[preset] || ImagePresets.full;
  return getOptimizedImageUrl(url, config);
};

export default getOptimizedImageUrl;
